import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {OptimizationCardScratchpadState} from '../../scratchpad/optimizationCardScratchpad.types';
import getOrCreateOptimizationCard from '../../utils/optimization/getOrCreateOptimizationCard';
import setOptimizeByTool from './setOptimizeByTool';
import setOptimizeAcrossTool from './setOptimizeAcrossTool';
import addOptimizationOutputTool from './addOptimizationOutputTool';
import updateOptimizationOutputTool from './updateOptimizationOutputTool';
import setOptimizationGoalTool from './setOptimizationGoalTool';
import getOptimizeByOptionsTool from './getOptimizeByOptionsTool';
import setAttributionSignalsTool from './setAttributionSignalsTool';
import getAttributionSignalsTool from './getAttributionSignalsTool';

const cardSpecificInstructions = `1. Use the ${V2CardAgentToolName.GetOptimizeByOptions} tool first to see available metrics for this campaign.
2. Use the ${V2CardAgentToolName.SetOptimizeBy} tool to set the optimization metric.
3. Use the ${V2CardAgentToolName.SetOptimizeAcross} tool to set the optimization grouping (Campaign/Placement/AdSet).
4. Use the ${V2CardAgentToolName.AddOptimizationOutput} tool to add new outputs (minimum 2 outputs required).
5. Use the ${V2CardAgentToolName.UpdateOptimizationOutput} tool to rename existing outputs.
6. Use the ${V2CardAgentToolName.SetOptimizationGoal} tool to set how many variants to serve in the end (goal). Optimization will gradually drop underperforming variants until the goal is reached, then optimize between the remaining variants.
7. If optimizing by Conversions or ClickThroughConversions, use ${V2CardAgentToolName.GetAttributionSignals} then ${V2CardAgentToolName.SetAttributionSignals}.

### Optimization card specific instructions
- Optimization cards do NOT have fallback outputs.
- Must have at least 2 outputs at all times.
- Use ${V2CardAgentToolName.AddOptimizationOutput} to add outputs with specific names.
- Use ${V2CardAgentToolName.UpdateOptimizationOutput} to rename existing outputs.
- Optimization goal (${V2CardAgentToolName.SetOptimizationGoal}) must be between 1 and the number of outputs; it defines how many variants are kept after underperformers are dropped.
- Attribution signals are only available for Conversions and ClickThroughConversions metrics.
- For ClickThroughConversions, only Click-type attribution signals are available.

### IMPORTANT
- If the user did not ask to change the optimize by or optimize across settings, skip those steps.
- Attribution signals are automatically cleared when switching to metrics that don't support them.`;

export default function addOrUpdateOptimizationCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, OptimizationCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId: cardId} = preProcessStandaloneAgent(params, store);

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateOptimizationCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.GENERAL_OPTIMIZATION]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.GENERAL_OPTIMIZATION,
        cardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getCardDetailsTool(),

        // Optimization tools
        getOptimizeByOptionsTool(store),
        setOptimizeByTool(),
        setOptimizeAcrossTool(),
        addOptimizationOutputTool(),
        updateOptimizationOutputTool(),
        setOptimizationGoalTool(),
        getAttributionSignalsTool(),
        setAttributionSignalsTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card: await getOrCreateOptimizationCard(cardId, store)};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateOptimizationCard,
    description:
      'Add or update an Optimization card. Automatically optimizes output distribution based on performance metrics (Clicks, Conversions, etc.).',
    displayName: 'Working on optimization card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
